import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { addContract } from '../../../../redux/Slices/ContractSlice';
import axios from 'axios';
import { BASE_URL, getToken } from '../../../../shared/utils.js/utils';
import { makeOfflineRequest } from '../../../../shared/utils.js/offlineRequestManager';
import { getDropdownCache, saveDropdownCache, generateDropdownCacheKey, isOnline, getAnyDropdownCache, getAllContractsCacheForQuery, saveContractsCache, generateContractsCacheKey } from '../../../../shared/utils.js/offlineQueue';






// normalize lsit
const normalizeListResponse = (payload) => {
  if (!payload) return { items: [], pagination: null };

  let items = [];
  let pagination = null;

  if (Array.isArray(payload?.data?.data)) {
    items = payload.data.data;
    pagination = payload.data?.meta || payload.data?.pagination || null;
  } else if (Array.isArray(payload?.data)) {
    items = payload.data;
    pagination = payload.meta || payload.pagination || null;
  } else if (Array.isArray(payload?.items)) {
    items = payload.items;
    pagination = payload.pagination || payload.meta || null;
  } else if (Array.isArray(payload)) {
    items = payload;
    pagination = null;
  } else {
    items = [];
    pagination = payload.pagination || payload.meta || null;
  }

  if (pagination) {
    pagination = {
      current_page: pagination.current_page || pagination.page || 1,
      last_page: pagination.last_page || pagination.total_pages || 1,
      per_page: pagination.per_page || 10,
      total: pagination.total || 0,
    };
  }

  return { items, pagination };
};

const AddContract = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    quotation_id: '',
    date: '',
    start_date: '',
    end_date: '',
    retention_percent: '',
    advance_payment_percent: '',
    notes: '',
  });

  // Responsive form width based on sidebar state
  useEffect(() => {
    const updateFormWidth = () => {
      const root = document.documentElement;
      const sidebar = document.querySelector('[data-sidebar]') || document.querySelector('.sidebar') || document.querySelector('[class*="sidebar"]');

      if (sidebar) {
        const isCollapsed = sidebar.classList.contains('collapsed') ||
          sidebar.getAttribute('data-collapsed') === 'true' ||
          window.getComputedStyle(sidebar).width === '0px' ||
          sidebar.offsetWidth < 100;

        if (isCollapsed) {
          root.style.setProperty('--form-max-width', '100%');
        } else {
          root.style.setProperty('--form-max-width', '700px');
        }
      } else {
        const body = document.body;
        const hasSidebar = body.classList.contains('sidebar-open') ||
          body.getAttribute('data-sidebar-open') === 'true';

        if (!hasSidebar) {
          root.style.setProperty('--form-max-width', '100%');
        } else {
          root.style.setProperty('--form-max-width', '700px');
        }
      }
    };

    updateFormWidth();

    const observer = new MutationObserver(updateFormWidth);
    const sidebar = document.querySelector('[data-sidebar]') || document.querySelector('.sidebar') || document.querySelector('[class*="sidebar"]') || document.body;

    if (sidebar) {
      observer.observe(sidebar, {
        attributes: true,
        attributeFilter: ['class', 'data-collapsed'],
        childList: false,
        subtree: false
      });
    }

    window.addEventListener('resize', updateFormWidth);
    window.addEventListener('sidebar-toggle', updateFormWidth);
    document.addEventListener('sidebar-collapsed', updateFormWidth);
    document.addEventListener('sidebar-expanded', updateFormWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateFormWidth);
      window.removeEventListener('sidebar-toggle', updateFormWidth);
      document.removeEventListener('sidebar-collapsed', updateFormWidth);
      document.removeEventListener('sidebar-expanded', updateFormWidth);
    };
  }, []);

  // Quotation state variable 
  const [quotationDropdownOpen, setQuotationDropdownOpen] = useState(false);
  const [quotationSearchTerm, setQuotationSearchTerm] = useState('');
  const [quotationOptions, setQuotationOptions] = useState([]);
  const [quotationPage, setQuotationPage] = useState(1);
  const [quotationPagination, setQuotationPagination] = useState(null);
  const quotationSearchInputRef = useRef(null);

  const fetchQuotations = useCallback(
    async (page = quotationPage, search = quotationSearchTerm) => {
      try {
        const queryParams = { page, per_page: 10, search: search || undefined };
        const cacheKey = generateDropdownCacheKey('quotations', queryParams);

        // Try load from cache first
        const cached = await getDropdownCache(cacheKey);
        if (cached) {
          setQuotationOptions(cached.items || []);
          setQuotationPagination(cached.pagination || null);
        }

        if (isOnline()) {
          const token = getToken();
          if (!token) throw new Error(t('contracts.add.messages.noAuthToken'));
          const response = await axios.get(`${BASE_URL}/quotations`, {
            params: queryParams,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          const { items, pagination: paginationMeta } = normalizeListResponse(response.data);

          await saveDropdownCache(cacheKey, items, paginationMeta);
          setQuotationOptions(items);
          setQuotationPagination(paginationMeta);
        } else if (!cached) {
          // Fallback to any cached quotations
          const anyCached = await getAnyDropdownCache('quotations');
          if (anyCached.length > 0) {
            setQuotationOptions(anyCached);
          }
        }
      } catch (error) {
        console.error('Fetch quotations error:', error);
      }
    },
    [t, isRTL]
  );

  useEffect(() => {
    if (quotationDropdownOpen) {
      fetchQuotations(quotationPage, quotationSearchTerm);
    }
  }, [quotationDropdownOpen, quotationPage, quotationSearchTerm]);

  useEffect(() => {
    if (quotationSearchTerm !== undefined && quotationDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setQuotationPage(1);
        fetchQuotations(1, quotationSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [quotationSearchTerm, quotationDropdownOpen]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      const isDropdownContainer = target.closest('[data-dropdown-container]');
      const isDropdownToggle = target.closest('[data-dropdown-toggle]');

      if (!isDropdownContainer && !isDropdownToggle) {
        if (quotationDropdownOpen) setQuotationDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [quotationDropdownOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      const data = {
        quotation_id: form.quotation_id || undefined,
        date: form.date || undefined,
        start_date: form.start_date || undefined,
        end_date: form.end_date || undefined,
        retention_percent: form.retention_percent ? Number(form.retention_percent) : undefined,
        advance_payment_percent: form.advance_payment_percent ? Number(form.advance_payment_percent) : undefined,
        notes: form.notes || undefined,
      };

      try {
        let url = '/contracts';
        const formDataObj = new FormData();

        if (form.quotation_id) {
          url = `/contracts/from-quotation/${form.quotation_id}`;
        }

        if (form.date) formDataObj.append('date', form.date);
        if (form.start_date) formDataObj.append('start_date', form.start_date);
        if (form.end_date) formDataObj.append('end_date', form.end_date);
        if (form.retention_percent) formDataObj.append('retention_percent', form.retention_percent);
        if (form.advance_payment_percent) formDataObj.append('advance_payment_percent', form.advance_payment_percent);
        if (form.notes) formDataObj.append('notes', form.notes);

        const result = await makeOfflineRequest(url, 'POST', formDataObj);
        toast.success(result?.message || t('contracts.add.messages.addSuccess'), { rtl: isRTL });
        navigate('/contracts');
      } catch (offlineError) {
        if (offlineError.message === 'OFFLINE_REQUEST_SAVED') {
          // Optimistically update cache (optional but good)
          try {
            const tempId = Date.now();
            const newContract = {
              ...data,
              id: tempId,
              is_offline: true,
              contract_no: `OFFLINE-${tempId}`
            };
            const allCachedResult = await getAllContractsCacheForQuery();
            const updatedContracts = [newContract, ...allCachedResult.items];
            const cacheKey = generateContractsCacheKey({ page: 1, per_page: 10 });
            await saveContractsCache(cacheKey, updatedContracts.slice(0, 10), null);
          } catch (cacheError) {
            console.error('Error updating cache optimistically:', cacheError);
          }

          toast.info(
            isRTL
              ? 'تم حفظ الطلب وسيتم إرساله تلقائياً عند عودة الاتصال بالإنترنت'
              : 'Request saved and will be sent automatically when connection is restored',
            { rtl: isRTL, autoClose: 5000 }
          );
          navigate('/contracts');
          return;
        }

        // Fallback to original Redux dispatch
        await dispatch(addContract(data)).unwrap();
        toast.success(t('contracts.add.messages.addSuccess'), { rtl: isRTL });
        navigate('/contracts');
      }
    } catch (error) {
      const errorData = error?.response?.data || error;
      let errorMessage = t('contracts.add.messages.addError', { defaultValue: 'Failed to add contract' });

      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData?.errors) {
        errorMessage = Object.values(errorData.errors).flat().join(', ');
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, { rtl: isRTL });
    } finally {
      setIsSubmitting(false);
    }
  };

  // get selected quotation
  const getSelectedQuotation = () => {
    if (!form.quotation_id) return null;
    const selected = quotationOptions.find((q) => String(q.id) === String(form.quotation_id));
    return selected;
  };

  const getSelectedQuotationCurrencyName = () => {
    const selected = getSelectedQuotation();
    if (!selected) return '';
    return (
      selected.currency_name ||
      selected.currency?.name_ar ||
      selected.currency?.name_en ||
      selected.currency?.code ||
      ''
    );
  };

  const getSelectedQuotationExchangeRate = () => {
    const selected = getSelectedQuotation();
    if (!selected) return '';
    return selected.exchange_rate ?? selected.currency?.exchange_rate ?? '';
  };

  return (
    <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ width: '100%', maxWidth: 'var(--form-max-width, 700px)', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          <h2 style={{ marginBottom: 0, fontSize: '20px' }}>{t('contracts.add.title')}</h2>
          <button type="button" style={{ ...secondaryButtonStyle, backgroundColor: '#666', border: 'none' }} onClick={() => navigate(-1)}>
            {t('common.back', { defaultValue: 'Back' })}
          </button>
        </div>
        <div style={{ ...cardStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ position: 'relative' }} data-dropdown-container>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                <span style={{ fontSize: 14 }}>{t('contracts.add.quotation')}</span>
                <div data-dropdown-toggle onClick={() => setQuotationDropdownOpen((prev) => !prev)} style={{ ...dropdownDisplayStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                  <span>
                    {getSelectedQuotation()
                      ? `${getSelectedQuotation().quotation_no || `#${getSelectedQuotation().id}`}${getSelectedQuotation().subject ? ` - ${getSelectedQuotation().subject}` : ''}${getSelectedQuotation().project ? ` - ${isRTL ? (getSelectedQuotation().project.name_ar || getSelectedQuotation().project.name_en || '') : (getSelectedQuotation().project.name_en || getSelectedQuotation().project.name_ar || '')}` : ''}`
                      : t('contracts.add.selectQuotation')}
                  </span>
                  <span style={{ fontSize: 12 }}>▼</span>
                </div>
                <DropdownWithSearch
                  isOpen={quotationDropdownOpen}
                  searchTerm={quotationSearchTerm}
                  onSearchChange={setQuotationSearchTerm}
                  searchInputRef={quotationSearchInputRef}
                  items={quotationOptions}
                  pagination={quotationPagination}
                  onPageChange={setQuotationPage}
                  onClose={() => setQuotationDropdownOpen(false)}
                  onSelect={(item) => {
                    setForm((prev) => ({ ...prev, quotation_id: String(item.id) }));
                    setQuotationDropdownOpen(false);
                    setQuotationSearchTerm('');
                    setQuotationPage(1);
                  }}
                  label={t('contracts.add.quotation')}
                  isRTL={isRTL}
                  i18n={i18n}
                />
              </label>
            </div>
            <div style={gridStyle}>
              <Input
                label={t('contracts.add.currencyName', { defaultValue: 'العملة' })}
                value={getSelectedQuotationCurrencyName()}
                disabled
                isRTL={isRTL}
              />
              <Input
                label={t('contracts.add.exchangeRate', { defaultValue: 'سعر الصرف' })}
                value={getSelectedQuotationExchangeRate()}
                disabled
                isRTL={isRTL}
              />
            </div>
            <div style={gridStyle}>
              <Input label={t('contracts.add.date')} type="date" name="date" value={form.date} onChange={handleChange} isRTL={isRTL} />
              <Input
                label={t('contracts.add.startDate')}
                type="date"
                name="start_date"
                value={form.start_date}
                onChange={handleChange}
                isRTL={isRTL}
              />
              <Input label={t('contracts.add.endDate')} type="date" name="end_date" value={form.end_date} onChange={handleChange} isRTL={isRTL} />
            </div>
            <div style={gridStyle}>
              <Input
                label={t('contracts.add.retentionPercent')}
                type="number"
                min="0"
                name="retention_percent"
                value={form.retention_percent}
                onChange={handleChange}
                isRTL={isRTL}
              />
              <Input
                label={t('contracts.add.advancePaymentPercent')}
                type="number"
                min="0"
                name="advance_payment_percent"
                value={form.advance_payment_percent}
                onChange={handleChange}
                isRTL={isRTL}
              />
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span>{t('contracts.add.notes')}</span>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={4}
                style={{
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid #333',
                  backgroundColor: '#1a1f2e',
                  color: 'white',
                  resize: 'vertical',
                  direction: isRTL ? 'rtl' : 'ltr',
                  textAlign: isRTL ? 'right' : 'left',
                }}
              />
            </label>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexDirection: isRTL ? 'row' : 'row-reverse' }}>
              <button type="submit" style={submitButtonStyle} disabled={isSubmitting}>
                {isSubmitting ? t('contracts.add.saving') : t('contracts.add.save')}
              </button>
              <button type="button" style={secondaryButtonStyle} onClick={() => navigate('/contracts')}>
                {t('contracts.add.cancel')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const Input = ({ label, isRTL = true, ...rest }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <span style={{ fontSize: 14 }}>{label}</span>
    <input
      {...rest}
      style={{
        padding: 12,
        borderRadius: 8,
        border: '1px solid #333',
        backgroundColor: '#1a1f2e',
        color: 'white',
        direction: isRTL ? 'rtl' : 'ltr',
        textAlign: isRTL ? 'right' : 'left',
      }}
    />
  </label>
);

const pageStyle = {
  padding: 30,
  backgroundColor: '#14263b',
  minHeight: 'calc(100vh - 80px)',
  color: 'white',
  display: 'flex',
  justifyContent: 'center',
};

const cardStyle = {
  width: '100%',
  maxWidth: 'var(--form-max-width, 700px)',
  backgroundColor: '#1b3350',
  borderRadius: 12,
  padding: 24,
  border: '1px solid #333',
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 16,
};

const submitButtonStyle = {
  backgroundColor: '#AC2000',
  color: 'white',
  border: 'none',
  padding: '12px 18px',
  borderRadius: 8,
  cursor: 'pointer',
  minWidth: 120,
};

const secondaryButtonStyle = {
  backgroundColor: '#666',
  color: 'white',
  border: 'none',
  padding: '10px 18px',
  borderRadius: 8,
  cursor: 'pointer',
  minWidth: 120,
  fontSize: '14px'
};

const dropdownDisplayStyle = {
  width: '100%',
  padding: '12px 14px',
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  color: 'white',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  textAlign: 'right',
};

const dropdownMenuStyle = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  zIndex: 20,
  padding: 12,
  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
};

const dropdownSearchStyle = {
  width: '100%',
  padding: 10,
  borderRadius: 6,
  border: '1px solid #333',
  backgroundColor: '#14263b',
  color: 'white',
  marginBottom: 10,
};

const dropdownEmptyStyle = {
  padding: 12,
  textAlign: 'center',
  color: '#ccc',
  fontSize: 13,
};

const DropdownWithSearch = ({
  isOpen,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  onPageChange,
  onSelect,
  onClose,
  label,
  isRTL = true,
  i18n,
}) => {
  const { t } = useTranslation();
  const dropdownIsRTL = isRTL !== undefined ? isRTL : (i18n?.language === 'ar');

  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, searchInputRef]);

  if (!isOpen) return null;

  return (
    <div style={{ ...dropdownMenuStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr' }} onClick={(e) => e.stopPropagation()}>
      <div style={{ padding: 8 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={`${t('contracts.dropdown.searchPlaceholder')} ${label}`}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 0);
          }}
          onFocus={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          autoFocus
          style={{ ...dropdownSearchStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}
        />
      </div>
      {pagination && (pagination.last_page > 1 || (pagination.last_page === 1 && pagination.current_page === 1)) && (
        <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#ccc', fontSize: 11, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}>{t('contracts.dropdown.page')} {pagination.current_page} {t('contracts.dropdown.of')} {pagination.last_page}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 5;
              let start = Math.max(1, current - Math.floor(showPages / 2));
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map((page) => (
                <button key={page} type="button" onClick={(e) => { e.stopPropagation(); onPageChange(page); }} style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}>{page}</button>
              ));
            })()}
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
          </div>
        </div>
      )}
      <div style={{ maxHeight: 300, overflowY: 'auto', paddingBottom: 4 }}>
        {(Array.isArray(items) ? items : []).length === 0 ? (
          <div style={{ ...dropdownEmptyStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}>{t('contracts.dropdown.noResults')}</div>
        ) : (
          (Array.isArray(items) ? items : []).map((item) => (
            <div
              key={item.id}
              style={{
                padding: '10px 12px',
                borderTop: '1px solid #333',
                cursor: 'pointer',
                direction: dropdownIsRTL ? 'rtl' : 'ltr',
                textAlign: dropdownIsRTL ? 'right' : 'left'
              }}
              onClick={() => {
                onSelect(item);
                onClose?.();
              }}
            >
              {formatQuotation(item, i18n?.language || 'ar')}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const formatQuotation = (quotation, language = 'ar') => {
  if (!quotation) return '—';
  const quotationNo = quotation.quotation_no || (quotation.id ? `#${quotation.id}` : '');
  const subject = quotation.subject || '';
  const isRTL = language === 'ar';
  const project = quotation.project ? ` - ${isRTL ? (quotation.project.name_ar || quotation.project.name_en || '') : (quotation.project.name_en || quotation.project.name_ar || '')}` : '';
  return `${quotationNo}${subject ? ` - ${subject}` : ''}${project}` || `#${quotation.id}`;
};

export default AddContract;

